import { useFormContext } from 'react-hook-form';
import { FormSelectionCardGroupField, LocalizedText, lt, useFeatureFlag } from '@mews/framework';
import { Container, Icon, IconName, Stack, Title } from '@optimus-web/core';
import { FeatureFlag, TextKey } from '@mews-commander/enums';
import { LocalizedSmallParagraph } from '@mews-commander/ui';
import { Option } from '@optimus-web/core/src/SelectionCardGroup/SelectionCardGroup';
import { TargetBillType } from '../../../../schemas';
import { RoutingRulesFormFields } from '../../utils';
import { CustomerProfileSearchField } from '../../../../../../components/form/hookFields/asyncSelectField/CustomerProfileSearchField/CustomerProfileSearchField';
import { aggregationTypeOptions, includeCompanyDetailsOption } from './constants';

const getTargetTypeOptions = (areCompaniesEnabled: boolean): Option[] => [
    {
        media: <Icon value={IconName.Profiles} variant="basic" />,
        label: lt(TextKey.RoutingRulesGuestBill),
        value: TargetBillType.GuestBill,
        name: TargetBillType.GuestBill,
    },
    {
        media: <Icon value={IconName.Companies} variant="basic" />,
        label: lt(TextKey.RoutingRulesCompanyBill),
        value: TargetBillType.CompanyBill,
        name: TargetBillType.CompanyBill,
        disabled: !areCompaniesEnabled,
    },
];

export const TargetBillFields = () => {
    const { watch } = useFormContext();
    const targetTypeValue = watch(RoutingRulesFormFields.targetType());
    const areCompaniesEnabled = watch(RoutingRulesFormFields.companiesEnabled());
    const granularRoutingRulesConditionsExtensionEnabled = useFeatureFlag(
        FeatureFlag.granularRoutingRulesConditionsExtensionEnabled
    );

    const guestProfiles = watch(RoutingRulesFormFields.guestProfiles());
    const guestProfilesEnabled = watch(RoutingRulesFormFields.guestProfilesEnabled());
    const hasGuestProfileCondition = guestProfilesEnabled && guestProfiles.length > 0;

    return (
        <>
            <Title level={2}>
                <LocalizedText textKey={TextKey.RoutingRulesFormStepNameTargetBill} />
            </Title>
            <LocalizedSmallParagraph textKey={TextKey.RoutingRulesFormFieldDescriptionTargetType} variant="secondary" />
            <FormSelectionCardGroupField
                type="radio"
                templateColumns="1fr 1fr"
                name={RoutingRulesFormFields.targetType()}
                options={getTargetTypeOptions(areCompaniesEnabled)}
            />
            {targetTypeValue === TargetBillType.CompanyBill && (
                <>
                    <LocalizedSmallParagraph
                        textKey={TextKey.RoutingRulesFormFieldDescriptionAggregationType}
                        variant="secondary"
                    />
                    <FormSelectionCardGroupField
                        type="radio"
                        name={RoutingRulesFormFields.aggregationType()}
                        options={aggregationTypeOptions}
                    />
                </>
            )}
            {targetTypeValue === TargetBillType.GuestBill && (
                <>
                    <FormSelectionCardGroupField
                        type="checkbox"
                        name={RoutingRulesFormFields.includeCompanyDetails()}
                        options={includeCompanyDetailsOption}
                    />

                    {granularRoutingRulesConditionsExtensionEnabled && hasGuestProfileCondition && (
                        <Container padding={['400', '0', '0', '0']}>
                            <Stack vertical spacing="300">
                                <Stack vertical spacing="100">
                                    <Title level={3}>
                                        <LocalizedText
                                            textKey={TextKey.RoutingRulesFormTargetBillStepGuestProfileTitle}
                                        />
                                    </Title>
                                    <LocalizedSmallParagraph
                                        textKey={TextKey.RoutingRulesFormTargetBillStepGuestProfileDescription}
                                        variant="secondary"
                                    />
                                </Stack>
                                <CustomerProfileSearchField
                                    required
                                    name={RoutingRulesFormFields.guestProfileTarget()}
                                    label={lt('RoutingRulesFormTargetBillStepGuestProfileFieldLabel')}
                                    placeholder={lt('RoutingRulesFormGuestProfilesSearchPlaceholder')}
                                    newCustomerButtonText={lt('RoutingRulesFormGuestProfilesAddNewGuestProfile')}
                                />
                                <Stack vertical spacing="200">
                                    <LocalizedSmallParagraph
                                        textKey={
                                            TextKey.RoutingRulesFormTargetBillStepGuestProfileAggregationTypeDescription
                                        }
                                        variant="secondary"
                                    />
                                    <FormSelectionCardGroupField
                                        type="radio"
                                        name={RoutingRulesFormFields.aggregationType()}
                                        options={aggregationTypeOptions}
                                    />
                                </Stack>
                            </Stack>
                        </Container>
                    )}
                </>
            )}
        </>
    );
};
